import { createContext, useContext, useReducer } from 'react';

export interface TonePad {
  id: string;
  color: string;
  tone: string; // path to sound file
  note: string; // musical note name
}

export interface GameState {
  sequence: TonePad[];
  playerSequence: TonePad[];
  currentRound: number;
  isPlaying: boolean;
  gamePhase: 'listening' | 'repeating' | 'feedback' | 'complete' | 'ready';
  difficulty: number;
  playbackSpeed: number;
  levelStartTime?: number; // Track when current level started
  stats: {
    accuracy: number;
    reactionTimes: number[];
    errorsPerRound: number[];
    completedSequences: number;
    levelTime?: number; // Time spent on current level
  };
}

const initialState: GameState = {
  sequence: [],
  playerSequence: [],
  currentRound: 1,
  isPlaying: false,
  gamePhase: 'ready',
  difficulty: 1,
  playbackSpeed: 1000, // milliseconds between tones
  levelStartTime: Date.now(), // Track when level started
  stats: {
    accuracy: 0,
    reactionTimes: [],
    errorsPerRound: [],
    completedSequences: 0,
  },
};

type GameAction =
  | { type: 'START_SEQUENCE' }
  | { type: 'ADD_TO_SEQUENCE'; pad: TonePad }
  | { type: 'PLAYER_INPUT'; pad: TonePad }
  | { type: 'CHECK_SEQUENCE' }
  | { type: 'ADJUST_DIFFICULTY'; change: number }
  | { type: 'RECORD_REACTION_TIME'; time: number }
  | { type: 'COMPLETE_ROUND' }
  | { type: 'SET_GAME_PHASE'; phase: 'listening' | 'repeating' | 'feedback' | 'complete' }
  | { type: 'RESET_GAME' }
  | { type: 'RESET_SEQUENCE' };

const gameReducer = (state: GameState, action: GameAction): GameState => {
  switch (action.type) {
    case 'START_SEQUENCE':
      return {
        ...state,
        sequence: [], // Clear the sequence
        playerSequence: [], // Clear player input
        isPlaying: true,
        gamePhase: 'listening', // Set to listening phase
        stats: {
          ...state.stats,
          errorsPerRound: [...state.stats.errorsPerRound, 0], // Initialize errors for new round
        }
      };
      
    case 'ADD_TO_SEQUENCE':
      return {
        ...state,
        sequence: [...state.sequence, action.pad],
        gamePhase: 'listening', // Ensure we stay in listening phase while adding
      };
      
    case 'SET_GAME_PHASE':
      return {
        ...state,
        gamePhase: action.phase,
        playerSequence: action.phase === 'repeating' ? [] : state.playerSequence
      };

    case 'PLAYER_INPUT':
      return {
        ...state,
        playerSequence: [...state.playerSequence, action.pad],
      };

    case 'CHECK_SEQUENCE':
      const isCorrect = state.playerSequence.every(
        (pad, index) => pad.id === state.sequence[index].id
      );
      
      // Check if sequence is complete
      const isComplete = state.playerSequence.length === state.sequence.length;
      
      // Calculate running accuracy for the current sequence
      const currentAccuracy = (state.playerSequence.filter((pad, index) => 
        pad.id === state.sequence[index].id
      ).length / state.sequence.length) * 100;
      
      if (!isCorrect) {
        return {
          ...state,
          stats: {
            ...state.stats,
            accuracy: currentAccuracy,
            errorsPerRound: [...state.stats.errorsPerRound, 1],
          },
          gamePhase: 'feedback',
        };
      }

      if (isComplete) {
        const newCompletedSequences = state.stats.completedSequences + 1;
        return {
          ...state,
          currentRound: state.currentRound + 1,
          stats: {
            ...state.stats,
            completedSequences: newCompletedSequences,
            accuracy: (newCompletedSequences / state.currentRound) * 100,
          },
          sequence: [],
          playerSequence: [],
          gamePhase: 'ready',
          isPlaying: false,
        };
      }

      return state;

    case 'ADJUST_DIFFICULTY':
      return {
        ...state,
        difficulty: Math.max(1, state.difficulty + action.change),
        playbackSpeed: Math.max(
          300,
          state.playbackSpeed - action.change * 100
        ),
      };

    case 'RECORD_REACTION_TIME':
      return {
        ...state,
        stats: {
          ...state.stats,
          reactionTimes: [...state.stats.reactionTimes, action.time],
        },
      };

    case 'COMPLETE_ROUND':
      // Calculate accuracy based on successful completion
      const newCompletedSequences = state.stats.completedSequences + 1;
      const accuracy = (newCompletedSequences / state.currentRound) * 100;
      
      // Calculate level time
      const levelTime = state.levelStartTime 
        ? Math.round((Date.now() - state.levelStartTime) / 1000) 
        : 0;
      
      return {
        ...state,
        currentRound: state.currentRound + 1,
        stats: {
          ...state.stats,
          completedSequences: newCompletedSequences,
          accuracy: accuracy,
          levelTime: levelTime,
        },
        sequence: [], // Clear sequence for next round
        playerSequence: [], // Clear player sequence for next round
        gamePhase: 'ready', // Set to ready for next round
        isPlaying: false, // Reset playing state to enable next round button
      };

    case 'RESET_SEQUENCE':
      return {
        ...state,
        playerSequence: [],
        gamePhase: 'repeating'
      };

    case 'RESET_GAME':
      return {
        ...initialState,
        levelStartTime: Date.now(), // Reset level timer
      };

    default:
      return state;
  }
};

const ToneGameContext = createContext<{
  state: GameState;
  dispatch: React.Dispatch<GameAction>;
} | null>(null);

export const ToneGameProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(gameReducer, initialState);

  return (
    <ToneGameContext.Provider value={{ state, dispatch }}>
      {children}
    </ToneGameContext.Provider>
  );
};

export const useToneGame = () => {
  const context = useContext(ToneGameContext);
  if (!context) {
    throw new Error('useToneGame must be used within a ToneGameProvider');
  }
  return context;
};